/**
 * Примитивы, из которых собраны все страницы.
 *
 * Два правила закреплены здесь, а не в головах:
 * 1. Состояние никогда не передаётся одним цветом. У каждой цветной точки
 *    рядом есть слово — иначе интерфейс не читается ни при дальтонизме, ни в
 *    чёрно-белой распечатке, ни краем глаза.
 * 2. У карточки показателя одна жёсткая иерархия: название → значение →
 *    изменение → контекст → микрограф. Плитки одинакового веса — главная
 *    причина, по которой дашборды не читаются.
 */

import type { ReactNode } from "react";
import { CONFIDENCE_TITLE, DASH, signedPct } from "../lib/format";
import type { Confidence, Severity } from "../lib/api";
import { Sparkline } from "./charts";
import { IconAlert, IconEmpty, IconInfo, IconLock } from "./icons";

export type Tone = "ok" | "warn" | "alert" | "muted" | "accent" | "brand";
export type Status = "ok" | "attention" | "problem" | "unknown";

// ── Контейнеры ──────────────────────────────────────────────────────────

export function Card({
  title,
  note,
  actions,
  children,
  className = "",
}: {
  title?: ReactNode;
  note?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`card ${className}`.trim()}>
      {(title || actions) && (
        <header className="card-head">
          {title && <h2>{title}</h2>}
          {note && <span className="card-note">{note}</span>}
          <span className="spacer" />
          {actions && <span className="card-actions">{actions}</span>}
        </header>
      )}
      {children}
    </section>
  );
}

/** Шапка страницы: что это за экран и что с ним можно сделать. */
export function PageHeader({
  eyebrow,
  title,
  lede,
  actions,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  lede?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="page-head">
      <div className="page-head-text">
        {eyebrow && <div className="page-eyebrow">{eyebrow}</div>}
        <h1>{title}</h1>
        {lede && <p>{lede}</p>}
      </div>
      {actions && <div className="page-head-actions">{actions}</div>}
    </div>
  );
}

/** Панель управления данными: период, сравнение, показатель. */
export function FilterBar({ children }: { children: ReactNode }) {
  return <div className="filterbar">{children}</div>;
}

export function FilterGroup({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <div className="filter-group">
      {label && <span className="filter-label">{label}</span>}
      {children}
    </div>
  );
}

// ── Значки состояния ────────────────────────────────────────────────────

export function Badge({ tone = "muted", children }: { tone?: Tone; children: ReactNode }) {
  return <span className={`badge ${tone}`}>{children}</span>;
}

const STATUS_WORD: Record<Status, string> = {
  ok: "в порядке",
  attention: "стоит посмотреть",
  problem: "нужно вмешаться",
  unknown: "пока неизвестно",
};
const STATUS_TONE: Record<Status, Tone> = {
  ok: "ok",
  attention: "warn",
  problem: "alert",
  unknown: "muted",
};

/** Состояние словом и цветом сразу — порознь они врут. */
export function StatusBadge({ status, label }: { status: Status; label?: string }) {
  return (
    <Badge tone={STATUS_TONE[status] ?? "muted"}>
      <span className={`status-dot ${status}`} />
      {label ?? STATUS_WORD[status] ?? status}
    </Badge>
  );
}

export function statusWord(status: string): string {
  return STATUS_WORD[status as Status] ?? status;
}

const CONFIDENCE_TONE: Record<string, Tone> = { high: "ok", medium: "warn", low: "muted", none: "muted" };

export function ConfidenceBadge({ level, reason }: { level: Confidence | string; reason?: string }) {
  return (
    <span title={reason || undefined}>
      <Badge tone={CONFIDENCE_TONE[level] ?? "muted"}>{CONFIDENCE_TITLE[level] ?? level}</Badge>
    </span>
  );
}

const SEVERITY_WORD: Record<Severity, string> = {
  alert: "вмешаться",
  warn: "проверить",
  info: "к сведению",
};

export function SeverityBadge({ level }: { level: Severity }) {
  return <Badge tone={level === "alert" ? "alert" : level === "warn" ? "warn" : "muted"}>{SEVERITY_WORD[level]}</Badge>;
}

// ── Изменение ───────────────────────────────────────────────────────────

/**
 * Дельта. Направление стрелки — это факт, а цвет — оценка, и они не одно и
 * то же: у оттока рост означает «хуже». Поэтому «куда» и «хорошо ли»
 * задаются раздельно.
 */
export function Delta({
  value,
  formatted,
  higherIsBetter = true,
  note,
  neutral = false,
}: {
  value: number | null | undefined;
  formatted?: string;
  higherIsBetter?: boolean;
  note?: ReactNode;
  neutral?: boolean;
}) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return <span className="delta">{DASH}</span>;
  }
  const good = higherIsBetter ? value > 0 : value < 0;
  const tone = neutral || value === 0 ? "" : good ? "up" : "down";
  return (
    <span className={`delta ${tone}`.trim()}>
      {value !== 0 && <span className="arrow">{value > 0 ? "▲" : "▼"}</span>}
      {formatted ?? signedPct(value, Math.abs(value) < 10 ? 1 : 0)}
      {note && <span className="delta-note">{note}</span>}
    </span>
  );
}

// ── Карточка показателя ─────────────────────────────────────────────────

export function MetricCard({
  name,
  value,
  unit,
  delta,
  context,
  spark,
  sparkColor,
  status,
  statusLabel,
  hero = false,
  small = false,
  icon,
  title,
}: {
  name: ReactNode;
  value: ReactNode;
  unit?: string;
  delta?: ReactNode;
  context?: ReactNode;
  spark?: (number | null)[];
  sparkColor?: string;
  status?: Status;
  statusLabel?: string;
  /** Главный показатель страницы — он один. */
  hero?: boolean;
  /** Здоровая область: та же карточка, но тише. Иерархия важнее симметрии. */
  small?: boolean;
  icon?: ReactNode;
  /** Полный текст, который не поместился: подсказкой, а не мелким шрифтом. */
  title?: string;
}) {
  const flagged = status === "problem" || status === "attention";
  return (
    <article
      className={[
        "metric",
        hero ? "hero" : "",
        small ? "small" : "",
        status ? `tone-${status}` : "",
        flagged ? "flagged" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      title={title}
    >
      <div className="metric-name">
        {icon}
        <span>{name}</span>
      </div>
      <div className="metric-value">
        {value}
        {unit && <span className="unit">{unit}</span>}
      </div>
      {(status || delta) && (
        <div className="metric-meta">
          {/* Состояние — значком «точка + слово»: цвет в одиночку ничего не
              значит ни при дальтонизме, ни краем глаза. */}
          {status && <StatusBadge status={status} label={statusLabel} />}
          {delta}
        </div>
      )}
      {context && <div className="metric-context">{context}</div>}
      {spark && spark.filter((point) => point !== null).length > 1 && (
        <div className="metric-spark">
          <Sparkline values={spark} color={sparkColor} />
        </div>
      )}
    </article>
  );
}

// ── Переключатель ───────────────────────────────────────────────────────

export function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: T;
  options: { value: T; label: ReactNode; title?: string }[];
  onChange: (value: T) => void;
  ariaLabel?: string;
}) {
  return (
    <div className="segmented" role="group" aria-label={ariaLabel}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          title={option.title}
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

// ── Раскрывашка ─────────────────────────────────────────────────────────

/** Прогрессивное раскрытие вместо стены текста: «почему так» — по запросу. */
export function Why({ summary = "Почему так", children }: { summary?: string; children: ReactNode }) {
  return (
    <details className="why">
      <summary>{summary}</summary>
      <div className="inner">{children}</div>
    </details>
  );
}

// ── Состояния ───────────────────────────────────────────────────────────

export function Loading({ rows = 3 }: { rows?: number }) {
  return (
    <div className="stack" aria-busy="true" aria-live="polite">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="skeleton" style={{ height: index === 0 ? 38 : 18, width: index === 0 ? "46%" : undefined }} />
      ))}
    </div>
  );
}

/** Скелет страницы аналитики: шапка, ряд показателей, график. */
export function LoadingDashboard() {
  return (
    <div className="stack" aria-busy="true" aria-live="polite" style={{ gap: "var(--s5)" }}>
      <div className="skeleton" style={{ height: 104, borderRadius: "var(--r-container)" }} />
      <div className="grid three">
        {[0, 1, 2].map((index) => (
          <div key={index} className="skeleton" style={{ height: 116, borderRadius: "var(--r-card)" }} />
        ))}
      </div>
      <div className="skeleton" style={{ height: 300, borderRadius: "var(--r-card)" }} />
    </div>
  );
}

export function ErrorBox({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div className="error-box" role="alert">
      <IconAlert size={17} style={{ flex: "none", marginTop: 1 }} />
      <span>{message}</span>
    </div>
  );
}

export function Notice({ tone = "info", children }: { tone?: "info" | "warn"; children: ReactNode }) {
  return (
    <div className={`notice ${tone === "info" ? "info" : ""}`.trim()}>
      <IconInfo size={17} style={{ flex: "none", marginTop: 1, color: tone === "info" ? "var(--accent)" : "var(--attention)" }} />
      <span>{children}</span>
    </div>
  );
}

export function Empty({
  title,
  children,
  actions,
  icon,
}: {
  title: string;
  children?: ReactNode;
  actions?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="state">
      <span className="state-icon">{icon ?? <IconEmpty size={22} />}</span>
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {actions && <div className="actions">{actions}</div>}
    </div>
  );
}

export function Paywall({ what }: { what: string }) {
  return (
    <Card>
      <Empty title="Раздел платного тарифа" icon={<IconLock size={22} />}>
        {what} входит в «Детальную аналитику». На бесплатном тарифе доступно «Здоровье
        канала» — оно отвечает на вопрос «всё ли в порядке», но не ищет возможности роста.
      </Empty>
    </Card>
  );
}
